/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */
import React from 'react';
import { RootElement } from './src/components/root-element';
// You can delete this file if you're not using it
import '@fontsource/open-sans/300.css';
import '@fontsource/open-sans/400.css';
import '@fontsource/open-sans/700.css';
import './src/styles/theme.css';
import './src/styles/global.css';

export const wrapRootElement = ({ element }) => {
  return <RootElement>{element}</RootElement>;
};

const revealGatsbyImage = (img) => {
  if (img.complete && img.naturalWidth > 0) {
    img.style.opacity = '1';
    const wrapper = img.closest('[data-gatsby-image-wrapper]');
    const placeholder = wrapper && wrapper.querySelector('[data-placeholder-image]');
    if (placeholder) {
      placeholder.style.opacity = '0';
    }
  }
};

const swapLazyImageSources = (img) => {
  // Mirror gatsby-plugin-image's native lazy-load snippet. Usercentrics rewrites
  // that inline <script type="module"> to <script type="text/plain">, so lazy
  // images are left with only data-src and never start loading.
  if (img.dataset.src) {
    img.setAttribute('src', img.dataset.src);
    img.removeAttribute('data-src');
  }
  if (img.dataset.srcset) {
    img.setAttribute('srcset', img.dataset.srcset);
    img.removeAttribute('data-srcset');
  }
  const picture = img.parentNode;
  if (picture) {
    picture.querySelectorAll('source[data-srcset]').forEach((source) => {
      source.setAttribute('srcset', source.dataset.srcset);
      source.removeAttribute('data-srcset');
    });
  }
};

const forceImageSwap = () => {
  try {
    document.querySelectorAll('img[data-main-image]').forEach((img) => {
      swapLazyImageSources(img);
      revealGatsbyImage(img);
    });
  } catch (e) {
    console.log(e);
  }
};

// Poll for a short window because the `load` event on native lazy-loaded images
// isn't reliably observable after a manual data-src swap. Polling is idempotent
// and cheap, and guarantees every loaded image is revealed.
let pollUntil = 0;
let pollTimer = null;

const pollImages = (durationMs) => {
  pollUntil = Math.max(pollUntil, Date.now() + durationMs);
  if (pollTimer) {
    return;
  }
  pollTimer = setInterval(() => {
    forceImageSwap();
    if (Date.now() >= pollUntil) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }, 400);
};

export const onInitialClientRender = () => {
  // Reveal an image as soon as it finishes loading (bonus path).
  document.body.addEventListener(
    'load',
    (event) => {
      const target = event.target;
      if (
        target &&
        target.tagName === 'IMG' &&
        target.hasAttribute('data-main-image')
      ) {
        revealGatsbyImage(target);
      }
    },
    true
  );

  // When the user scrolls, images lazily load; reveal them and keep polling
  // briefly to catch images that finish right after the scroll.
  window.addEventListener(
    'scroll',
    () => {
      forceImageSwap();
      pollImages(3000);
    },
    { passive: true }
  );

  forceImageSwap();
  pollImages(12000);
};

export const onRouteUpdate = ({ location }) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'gatsby-route-change',
    page: location.pathname + location.search,
    pageTitle: document.title,
  });

  forceImageSwap();
  pollImages(12000);
};
